import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FwPopoverComponent } from '@flywheel-io/vision';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { FileStatus } from '../local-imports/local-import.enum';
import { AddDataDialogData, AddDataDialogResult, ImportFileInfo } from '../local-imports/local-import.interface';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { generateImportLabel } from '../../shared/imports.utils';
import { DeidProfile } from '../../shared/models/dicom.model';
import { ImportConflictStrategy, ImportMissingMeta, XferOperationStatus, XferStorage } from '../../shared/models/xfer.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { Router } from '@angular/router';
import { LOCAL_STORAGE_ID } from '../../xfer/components/operation-storage-select/operation-storage-select.constants';

@Component({
  host: { 'test-id': 'add-data-dialog' },
  selector: 'add-data-dialog',
  styleUrls: ['./add-data-dialog.component.scss'],
  templateUrl: './add-data-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AddDataDialog implements OnInit, OnDestroy {
  @ViewChild('popoverTrigger') popoverTrigger: ElementRef;
  @ViewChild(FwPopoverComponent) popover: FwPopoverComponent;

  protected completedFiles = this.data.completedFiles ?? 0;
  protected isRemovingBlobs = false;
  protected failedFiles = this.data.failedFiles ?? 0;
  protected uploadedBlobsIds: Record<string, string[]> = this.data.uploadedBlobsIds ?? {};
  protected filesInfo: ImportFileInfo[] = this.data.filesInfo ?? [];
  protected blobSessionId: string;
  protected selectedStorage: string;
  protected webUploaderV2Enabled = false;
  protected deidProfileIsLoaded = false;

  protected readonly permissionAction = PermissionAction;
  protected readonly status = XferOperationStatus;
  protected readonly localImport = LOCAL_STORAGE_ID;
  protected readonly deidProfile$: Observable<DeidProfile> = of(this.config.isFeatureEnabled('deid_profile')).pipe(
    switchMap(isEnabled => {
      if (!isEnabled) {
        return of(null);
      } else {
        return this.coreApi.projects.settings.deidProfile.get(this.data.projectId).pipe(
          catchError((error) => {
            this.logger.error(error);
            return of(null);
          }),
          tap(() => this.deidProfileIsLoaded = true),
        );
      }
    })
  );

  protected isLoading$ = new BehaviorSubject<boolean>(false);
  protected errorText = '';

  private selectedStorageMessages = TranslationBundle.EMPTY
  private subscriptions = {
    selectedStorageMessages: Subscription.EMPTY,
    localImportMessages: Subscription.EMPTY,
  };

  constructor(
    @Inject(DIALOG_DATA) protected data: AddDataDialogData,
    protected config: ConfigService,
    public dialogRef: DialogRef<AddDataDialogResult, AddDataDialog>,
    private translate: TranslationService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private router: Router,
    private notification: NotificationService,
  ) {
    this.selectedStorage = this.localImport;
    this.coreApi.xfer.blobs.createSession({ project_id: this.data.projectId, local_path: 'Browser Upload' }).subscribe({
      next: (sess) => { this.blobSessionId = sess._id; },
      error: (error) => { this.logger.error(error); },
    });
  }

  get filesInProgress(): Array<ImportFileInfo> {
    return this.filesInfo.filter(file => file.status === FileStatus.InProgress);
  }

  get isCompleteUploadDisabled(): boolean {
    return this.filesInProgress.length > 0 || this.filesInfo.length === 0 || this.isRemovingBlobs || !this.filesInfo.some(file => file.status === FileStatus.Complete);
  }

  get isLocalUpload(): boolean {
    return this.selectedStorage === this.localImport;
  }

  get isSubmitButtonDisabled(): boolean {
    return this.isLocalUpload ? this.isCompleteUploadDisabled : !this.selectedStorage;
  }

  get isUnsavedChanges(): boolean {
    return this.isLocalUpload && this.filesInfo.length !== 0;
  }

  ngOnInit(): void {
    this.subscriptions.selectedStorageMessages = this.translate.getBundle('projectImports.addDataDialog').subscribe(messages => this.selectedStorageMessages = messages);
    this.webUploaderV2Enabled = this.config.isFeatureEnabled('web_uploader_v2');
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  closePopover(): void {
    if (!this.popover.isOpen) {
      return;
    }

    this.popoverTrigger.nativeElement.click();
  }

  protected onSubmit(): void {
    if (this.isLocalUpload) {
      this.onCompleteLocalUpload();
    } else {
      this.onCompleteExternalUpload();
    }
    this.router.navigate(['projects', this.data.projectId, 'imports']);
    this.notification.success('projectImport.create.success');
  }

  protected onCancel(): void {
    if (this.isUnsavedChanges) {
      this.popoverTrigger.nativeElement.click();
      return;
    }
    this.cancelUpload();
  }

  protected cancelUpload(): void {
    const cleanUp = this.blobSessionId ? this.coreApi.xfer.blobs.deleteSession(this.blobSessionId) : of(null);

    cleanUp.subscribe(() => {
      this.dialogRef.close();
    });
  }

  protected onCompleteExternalUpload(): void {
    const data = {
      createPayload: {
        label: generateImportLabel(this.selectedStorageMessages.get('importViaCoreWebApp')),
        refs: {
          project: this.data.projectId,
          storage: this.selectedStorage,
        },
        missing_meta: ImportMissingMeta.Fail,
        conflict_strategy: ImportConflictStrategy.Review,
      },
    };

    this.importData(data);
  }

  protected onCompleteLocalUpload(): void {
    const data = {
      createPayload: {
        label: generateImportLabel(this.selectedStorageMessages.get('importViaCoreWebApp')),
        refs: {
          project: this.data.projectId,
          blob_session: this.blobSessionId,
        },
        missing_meta: ImportMissingMeta.Fail,
        conflict_strategy: ImportConflictStrategy.Review,
        deid: false, // already de-identified client-side
      },
    };

    this.coreApi.xfer.blobs.finishSession(this.blobSessionId).subscribe(() => {
      this.importData(data);
    });
  }

  private importData(payload: AddDataDialogResult): void {
    this.isLoading$.next(true);
    this.errorText = '';
    this.coreApi.xfer.imports.run(payload.createPayload).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.errorText = error.error.message;
        this.isLoading$.next(false);
        return of(null);
      }),
    ).subscribe((val) => {
      if (val) {
        this.isLoading$.next(false);
        this.dialogRef.close(payload);
      }
    });
  }

  protected onStorageSelect(storage: XferStorage): void {
    this.selectedStorage = storage?._id
  }
}
